import React, { useMemo } from 'react';

import { Surface } from '@infoods/lifebase-ui';

import ShowChartIcon from '@mui/icons-material/ShowChartRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import WindowOutlinedIcon from '@mui/icons-material/WindowOutlined';

import classes from './InbodyIndicators.module.scss';
import {
  LbTabPanel,
  LbTabs,
  LbToggleTabsList,
  TabsConfig,
} from '../../../../../../shared/components/tabs';
import { InbodyIndicatorsTableView } from './InbodyIndicatorsTableView/InbodyIndicatorsTableView';
import { InbodyIndicatorsChartView } from './InbodyIndicatorsChartView/InbodyIndicatorsChartView';
import { InbodyIndicatorsGroupedView } from './InbodyIndicatorsGroupedView/InbodyIndicatorsGroupedView';
import { InbodyIndicatorInfo } from '../../../../../interfaces/inbodyIndicator';
import { Option } from '../../../../../../general-types';
import { InbodyIndicatorsOptions } from '../InbodyIndicatorsOptions/InbodyIndicatorsOptions';
import { MealPlanOnScanDayInfo } from '../../../../../interfaces/mealPlanOnScanDayInfo';
import { INBODY_TYPES } from '../../../../../../shared/constants/inbody-info/InbodyType';
import { getFlagsObject } from '../../../../../../shared/utils/sortingAndGroupingUtil';
import { CustomerInfo } from '../NgzInbodyIndicatorsTab.settings';
import { ScanMetadata } from '../../../../../interfaces/scanMetadata';
import { CustomerInfoSection } from './CustomerInfoSection/CustomerInfoSection';
import { FilterSection } from './FilterSection/FilterSection';
import { CustomerReportsRow } from './CustomerReportsRow/CustomerReportsRow';
import { CustomerType } from '../../../../../../shared/constants/customer/customerType';
import { EmptyList } from '../../../../../../shared/components/ui-components';

const CheckInViewTabs = {
  GROUPED: 'GROUPED',
  CHART: 'CHART',
  TABLE: 'TABLE',
} as const;
const { GROUPED, CHART, TABLE } = CheckInViewTabs;

type InbodyIndicatorsProps = {
  customerInbodyIndicators: InbodyIndicatorInfo[];
  /* all scans for the current inbody type, not narrowed by the selected date range */
  allInbodyIndicators: InbodyIndicatorInfo[];
  mealPlansOnScanDay: MealPlanOnScanDayInfo[];
  inbodyTypeOptions: Option[];
  availableScansMetadata: ScanMetadata[];
  customerInfo: CustomerInfo;
  customerType: CustomerType;
  showAllScans: boolean;
  entityId?: number;
  onToggleShowAllScans: (checked: boolean) => void;
};

const InbodyIndicators = ({
  customerInbodyIndicators,
  allInbodyIndicators,
  mealPlansOnScanDay,
  inbodyTypeOptions,
  availableScansMetadata,
  customerInfo,
  customerType,
  showAllScans,
  entityId,
  onToggleShowAllScans,
}: InbodyIndicatorsProps) => {
  const inbodyTypeFlags = useMemo(
    () => getFlagsObject(INBODY_TYPES, [customerInfo.inbodyType]),
    [customerInfo.inbodyType],
  );

  const tabsConfigs: TabsConfig[] = [
    {
      icon: <WindowOutlinedIcon fontSize="inherit" />,
      value: GROUPED,
      panel: (
        <InbodyIndicatorsGroupedView
          customerInbodyIndicators={customerInbodyIndicators}
          mealPlansOnScanDay={mealPlansOnScanDay}
          customerInbodyTypeFlags={inbodyTypeFlags}
        />
      ),
    },
    {
      icon: <ShowChartIcon fontSize="inherit" />,
      value: CHART,
      panel: (
        <InbodyIndicatorsChartView
          customerInbodyIndicators={customerInbodyIndicators}
          mealPlansOnScanDay={mealPlansOnScanDay}
          customerInbodyTypeFlags={inbodyTypeFlags}
        />
      ),
    },
    {
      icon: <FormatListBulletedRoundedIcon fontSize="inherit" />,
      value: TABLE,
      panel: (
        <InbodyIndicatorsTableView customerInbodyIndicators={customerInbodyIndicators} />
      ),
    },
  ];

  const defaultTab = inbodyTypeFlags.NON_INBODY ? TABLE : GROUPED;

  const hasAnyScans = customerInbodyIndicators.some(({ scans }) => scans?.length);

  return (
    <LbTabs defaultValue={defaultTab}>
      <Surface className={classes.topBlock}>
        <div className={classes.leftItems}>
          <CustomerInfoSection
            customerInfo={customerInfo}
            inbodyTypeFlags={inbodyTypeFlags}
          />
        </div>
        <div className={classes.rightItems}>
          <FilterSection
            inbodyTypeFlags={inbodyTypeFlags}
            inbodyTypeOptions={inbodyTypeOptions}
            availableScansMetadata={availableScansMetadata}
            showAllScans={showAllScans}
            onToggleShowAllScans={onToggleShowAllScans}
          />
          <div className={classes.options}>
            <LbToggleTabsList
              tabsConfig={tabsConfigs}
              size="small"
              className={classes.tabsList}
            />
            <InbodyIndicatorsOptions
              customerInfo={customerInfo}
              availableScansMetadata={availableScansMetadata}
              customerInbodyIndicators={allInbodyIndicators}
              customerInbodyTypeFlags={inbodyTypeFlags}
              customerType={customerType}
              entityId={entityId}
            />
          </div>
        </div>
      </Surface>
      {!inbodyTypeFlags.NON_INBODY && (
        <CustomerReportsRow
          customerInfo={customerInfo}
          customerType={customerType}
          entityId={entityId}
          availableScansMetadata={availableScansMetadata}
        />
      )}
      {hasAnyScans ? (
        <LbTabPanel tabsConfig={tabsConfigs} />
      ) : (
        <EmptyList show description="There are no scans" />
      )}
    </LbTabs>
  );
};

export { InbodyIndicators };
